import api from './api';
import feedback from '@components/ui/general/feedback';

/**
 * MetadataApi - Handles fetching game metadata and rules from backend
 * 
 * Game metadata includes:
 * - Stat constraints (min/max values)
 * - Game rules and configuration
 * - Combat formulas
 * - Effect stacking rules
 */

/**
 * Fetch game metadata including stat constraints and rules
 * @returns {Promise<object>} The metadata object with stat_constraints, game_rules, etc.
 * @throws {Error} If metadata cannot be fetched (critical error)
 */
export const getGameMetadata = async () => {
  try {
    const response = await api.get('/api/game/metadata');
    
    if (!response.data) {
      throw new Error('No metadata received from server');
    }
    
    //console.info('[MetadataApi] Game metadata loaded successfully', response.data);
    return response.data;
    
  } catch (error) {
    // This is a critical error - game cannot function without metadata
    console.error('[CRITICAL] Failed to fetch game metadata!', error);
    console.error('Game rules unavailable. Cannot continue.');
    
    // Show user-facing error
    feedback.error('Failed to load game rules. Please refresh the page.');
    
    // Re-throw the error so calling code knows it failed
    throw error;
  }
};

/**
 * Validate that metadata has required structure
 * @param {object} metadata - The metadata object to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateMetadata = (metadata) => {
  if (!metadata) {
    console.error('[MetadataApi] Metadata is null or undefined');
    return false;
  }
  
  if (!metadata.stat_constraints) {
    console.error('[MetadataApi] Missing stat_constraints in metadata');
    return false;
  }
  
  // Check that critical stats have constraints defined
  const requiredStats = ['hp', 'ap', 'armor', 'strength', 'will'];
  for (const stat of requiredStats) {
    if (!metadata.stat_constraints[stat]) {
      console.error(`[MetadataApi] Missing constraint for required stat: ${stat}`);
      return false;
    }
  }
  
  return true;
};

/**
 * Get constraint for a specific stat
 * @param {object} metadata - The metadata object
 * @param {string} stat - The stat name
 * @returns {object} Constraint object with min/max values
 */
export const getStatConstraint = (metadata, stat) => {
  if (!metadata?.stat_constraints?.[stat]) {
    console.warn(`[MetadataApi] No constraint found for stat: ${stat}`);
    return { min: null, max: null };
  }
  
  return metadata.stat_constraints[stat];
};

/**
 * Apply constraint to a value
 * @param {number} value - The value to constrain
 * @param {object} constraint - The constraint object with min/max
 * @returns {number} The constrained value
 */
export const applyConstraint = (value, constraint) => {
  if (!constraint) return value;
  
  let result = value;
  
  // Apply minimum constraint
  if (constraint.min !== null && constraint.min !== undefined) {
    result = Math.max(constraint.min, result);
  }
  
  // Apply maximum constraint
  if (constraint.max !== null && constraint.max !== undefined) {
    result = Math.min(constraint.max, result);
  }
  
  return result;
};